import React, { useState, useContext, useEffect } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  Button,
  Grid2 as Grid,
  InputLabel,
  Typography,
  useTheme,
  TextField,
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import * as yup from 'yup'
import { ScoreContext, ScoreContextType } from '..'
import { Subscores } from 'utils/__generated__/graphql'

const schema = yup.object().shape({
  subscores: yup.array().of(
    yup.object().shape({
      value: yup
        .number()
        .min(1, 'Score must be at least 1')
        .max(10, 'Score cannot exceed 10')
        .required('Score is required'),
      description: yup.string().optional(),
    })
  ),
  user: yup.string().required(),
  company: yup.string().required(),
  site: yup.string().required(),
})

type AddScoreDataType = yup.InferType<typeof schema>

interface ScoreFormProps {
  open: boolean
  onClose: () => void
  onScoreSubmit: (data: AddScoreDataType) => void
}

interface Subscore {
  label: string
  key: string
  value?: number
  description?: string
  info?: string
  hstDescription?: string
  disabled?: boolean
  hstScore?: number
  autofocus?: boolean
}

const subscoresData: Subscore[] = [
  {
    key: 'analyze',
    label: 'Analyze',
    disabled: true,
  },
  {
    key: 'interconnect',
    label: 'Interconnection',
  },
  {
    key: 'offtake',
    label: 'Offtake',
  },
  {
    key: 'permitting',
    label: 'Permitting',
  },
  {
    key: 'landowner',
    label: 'Landowner Participation',
  },
  {
    key: 'taxCredits',
    label: 'Tax Credits',
  },
  {
    key: 'buildability',
    label: 'Buildability',
  },
]

function AddScoreDialog({ open, onClose, onScoreSubmit }: ScoreFormProps) {
  const { control, handleSubmit, watch, setValue } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      subscores: Array.from(Array(subscoresData.length)).map(() => ({
        value: 0,
        description: '',
      })),
    },
  })

  const { companies, sites } = useContext<ScoreContextType>(ScoreContext)

  const selectedCompanyId = watch('company')

  const selectedCompany = companies.find(company =>
    Object.is(company.id, selectedCompanyId)
  )

  const selectedSiteId = watch('site')

  const selectedSite = sites.find(site => Object.is(selectedSiteId, site.id))

  useEffect(() => {
    if (selectedSite) {
      const siteSubscores = Object.keys(selectedSite?.score?.subscores || {})
        .filter(scKey => !Object.is(scKey, '__typename'))
        .map(subscoreKey => {
          const subscoreObj = {
            ...(selectedSite?.score?.subscores?.[
              subscoreKey as keyof Subscores
            ] as Subscore),
            key: subscoreKey,
          }

          return subscoreObj
        })

      // Create a map of keys to their index positions based on subscoresData
      const orderMap: { [key: string]: number } = subscoresData.reduce(
        (map, obj, index) => {
          map[obj.key] = index
          return map
        },
        {} as { [key: string]: number }
      )

      // Sort siteSubscores based on the order in subscoresData
      siteSubscores.sort((a, b) => orderMap[a.key] - orderMap[b.key])

      const valuesToSet = siteSubscores.map(subscore => ({
        value: Object.is(subscore.key, 'analyze')
          ? Math.round(subscore.value || 0)
          : subscore.value || 0,
        description: subscore.description || '',
        key: subscore.key,
      }))

      setValue('subscores', valuesToSet)
    }
  }, [selectedSiteId, setValue])

  const companyOptions = companies.map(company => ({
    label: company.name,
    value: company.id,
  }))

  const userOptions = (selectedCompany?.users || []).map(user => ({
    label: `${user?.firstName} ${user?.lastName}`,
    value: user?.id,
  }))

  const siteOptions = sites
    .filter(s => !!s?.score?.subscores)
    .map(site => ({
      label: site.name,
      value: site.id,
    }))

  const theme = useTheme()

  const formatDataAndSubmit = (data: AddScoreDataType) => {
    const formattedData = data.subscores?.reduce((acc: any, item: any) => {
      acc[item.key] = {
        value: item.value,
        description: item.description,
      }
      return acc
    }, {} as Subscores)

    onScoreSubmit({ ...data, subscores: formattedData })
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Add Score</DialogTitle>
      <DialogContent>
        <Box component={`form`}>
          <Stack spacing={2}>
            <Divider
              textAlign="left"
              sx={{ color: theme.palette.primary.light }}
            >
              Details
            </Divider>
            <Grid container spacing={2}>
              <Grid size={6}>
                <Controller
                  name="company"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Box>
                      <InputLabel sx={{ m: 0 }} htmlFor={'company'}>
                        <Typography
                          sx={{
                            ...theme.typography.caption,
                            color: theme.palette.primary.light,
                          }}
                        >
                          Company
                        </Typography>
                      </InputLabel>
                      <Autocomplete
                        onChange={(_, newValue) =>
                          field.onChange(newValue?.value)
                        }
                        disablePortal
                        onInputChange={(_, _val, reason) => {
                          if (Object.is(reason, 'reset')) setValue('user', '')
                        }}
                        options={companyOptions}
                        getOptionLabel={option => option.label}
                        renderInput={params => (
                          <TextField
                            error={!!error}
                            helperText={error?.message}
                            {...params}
                            FormHelperTextProps={{
                              sx: { m: 0 },
                            }}
                          />
                        )}
                      />
                    </Box>
                  )}
                />
              </Grid>
              <Grid size={6}>
                <Controller
                  name="user"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Box>
                      <InputLabel sx={{ m: 0 }} htmlFor={'user'}>
                        <Typography
                          sx={{
                            ...theme.typography.caption,
                            color: theme.palette.primary.light,
                          }}
                        >
                          User
                        </Typography>
                      </InputLabel>
                      <Autocomplete
                        onChange={(_, newValue) =>
                          field.onChange(newValue?.value)
                        }
                        disablePortal
                        disabled={!selectedCompanyId}
                        options={userOptions}
                        getOptionLabel={option => option.label}
                        renderInput={params => (
                          <TextField
                            {...params}
                            error={!!error}
                            helperText={error?.message}
                            FormHelperTextProps={{
                              sx: { m: 0 },
                            }}
                          />
                        )}
                      />
                    </Box>
                  )}
                />
              </Grid>
              <Grid size={12}>
                <Controller
                  name="site"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Box>
                      <InputLabel sx={{ m: 0 }} htmlFor={'site'}>
                        <Typography
                          sx={{
                            ...theme.typography.caption,
                            color: theme.palette.primary.light,
                          }}
                        >
                          Listing
                        </Typography>
                      </InputLabel>
                      <Autocomplete
                        onChange={(_, newValue) =>
                          field.onChange(newValue?.value)
                        }
                        disablePortal
                        options={siteOptions}
                        getOptionLabel={option => option?.label || ''}
                        renderInput={params => (
                          <TextField
                            {...params}
                            error={!!error}
                            helperText={error?.message}
                            FormHelperTextProps={{
                              sx: { m: 0 },
                            }}
                          />
                        )}
                      />
                    </Box>
                  )}
                />
              </Grid>
            </Grid>
            <Divider
              textAlign="left"
              sx={{ color: theme.palette.primary.light }}
            >
              Subscores
            </Divider>
            {subscoresData.map((item, index) => (
              <Stack key={String(index)}>
                <Stack>
                  <InputLabel sx={{ m: 0 }} htmlFor={item.key}>
                    <Typography
                      sx={{
                        ...theme.typography.caption,
                        color: theme.palette.primary.light,
                      }}
                    >
                      {item.label}
                    </Typography>
                  </InputLabel>
                </Stack>
                <Grid container spacing={2}>
                  <Grid size={2}>
                    <Controller
                      name={`subscores.${index}.value` as const}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          disabled={item.disabled}
                          sx={{
                            background: 'white',
                          }}
                          inputProps={{
                            style: {
                              fontSize: 45,
                              padding: 0,
                              textAlign: 'center',
                              color: theme.palette.primary.light,
                            },
                            step: 1,
                            min: 1,
                            max: 10,
                          }}
                          error={!!error}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={10}>
                    <Controller
                      name={`subscores.${index}.description` as const}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          placeholder="Your notes..."
                          fullWidth
                          size="small"
                          multiline
                          rows={3}
                          sx={{ background: 'white' }}
                          variant="outlined"
                          inputProps={{
                            style: {
                              color: theme.palette.primary.light,
                              fontSize: theme.typography.caption.fontSize,
                              lineHeight: 1.3,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Stack>
            ))}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit(formatDataAndSubmit)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function AddScore() {
  const [open, setOpen] = useState(false)

  const handleClickOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleScoreSubmit = (data: AddScoreDataType) => console.log(data)

  return (
    <React.Fragment>
      <Button size="small" variant="contained" onClick={handleClickOpen}>
        + Add Score
      </Button>
      <AddScoreDialog
        open={open}
        onClose={handleClose}
        onScoreSubmit={handleScoreSubmit}
      />
    </React.Fragment>
  )
}

export default AddScore
